import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DateFormatPipe, StatusBadgePipe } from '../../../../shared/pipes';
import { FieldTest } from '../../../../core/data-models/field-test.model';
import { CustomFieldTest } from '../../../../core/data-models/custom-field-test.model';
import { CustomFieldTestType } from '../../../../core/data-models/custom-field-test-type.model';
import { FieldTestStatus } from '../../../../core/data-models/field-test-status.model';
import { FieldTestType } from '../../../../core/data-models/field-test-type.model';
import { FieldTestService } from '../../../../core/data-services/field-test.service';
import { FieldTestSubmissionService } from '../../../../core/data-services/field-test-submission.service';
import { DroneService } from '../../../../core/data-services/drone.service';
import { environment } from '../../../../../environments/environment';
import { openExternal } from '../../../../core/utils/external-link-utils';

@Component({
  selector: 'app-field-tests',
  standalone: true,
  imports: [CommonModule, DateFormatPipe, StatusBadgePipe],
  templateUrl: './field-tests.component.html',
  styleUrls: ['./field-tests.component.scss']
})
export class FieldTestsComponent implements OnInit {
  // Inject services
  private fieldTestService = inject(FieldTestService);
  private submissionService = inject(FieldTestSubmissionService);
  private droneService = inject(DroneService);

  // Signal-based state from service
  readonly fieldTests = this.fieldTestService.fieldTests;
  readonly customTests = this.submissionService.customTests;
  readonly isLoading = this.fieldTestService.isLoading;

  // Computed stats from service
  readonly passedCount = this.fieldTestService.passedCount;
  readonly failedCount = this.fieldTestService.failedCount;
  readonly pendingCount = this.fieldTestService.pendingCount;

  // View toggle (local state)
  activeTab = signal<'records' | 'templates'>('records');

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.fieldTestService.loadFieldTests();
    this.fieldTestService.loadStats();
    this.submissionService.loadCustomTests();
    // Load drones for name resolution
    this.droneService.loadOrganizationDrones();
  }

  uploadCustomTest(): void {
    this.router.navigate(['/logbook/testing/field/custom/add']);
  }

  viewCustomTest(test: CustomFieldTest): void {
    this.router.navigate(['/logbook/testing/field/custom', test.id]);
  }

  editCustomTest(test: CustomFieldTest): void {
    this.router.navigate(['/logbook/testing/field/custom', test.id, 'edit']);
  }

  deleteCustomTest(test: CustomFieldTest): void {
    // TODO: Implement delete confirmation modal
    this.submissionService.deleteCustomTest(test.id);
  }

  viewCustomTestFile(file: { name: string; filePath: string }): void {
    const fullUrl = this.getFullUrl(file.filePath);
    openExternal(fullUrl);
  }

  /**
   * Get full CDN URL from file path
   */
  private getFullUrl(filePath: string): string {
    if (!filePath) return '';

    // If already a full URL (backwards compatibility), return as-is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }

    // Prepend CDN URL
    const cdnUrl = environment.appCDN.endsWith('/')
      ? environment.appCDN.slice(0, -1)
      : environment.appCDN;
    const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    return `${cdnUrl}/${cleanPath}`;
  }

  getCustomTestTypeIcon(type: CustomFieldTestType | null | undefined): string {
    return this.submissionService.getCustomTestTypeIcon(type);
  }

  getCustomTestTypeLabel(type: CustomFieldTestType | null | undefined): string {
    return this.submissionService.getCustomTestTypeLabel(type);
  }

  getCategoryLabel(category: string | null | undefined): string {
    return this.submissionService.getCategoryLabel(category);
  }

  getFrequencyLabel(frequency: string | null | undefined): string {
    return this.submissionService.getFrequencyLabel(frequency);
  }

  getFileIcon(type: string): string {
    return this.submissionService.getFileIcon(type);
  }

  addFieldTest(): void {
    this.router.navigate(['/logbook/testing/field/add']);
  }

  viewTest(test: FieldTest): void {
    this.router.navigate(['/logbook/testing/field', test.id]);
  }

  editTest(test: FieldTest): void {
    this.router.navigate(['/logbook/testing/field', test.id, 'edit']);
  }

  deleteTest(test: FieldTest): void {
    // TODO: Implement delete confirmation modal
    this.fieldTestService.deleteFieldTest(test.id);
  }

  getStatusText(status: FieldTestStatus): string {
    return this.fieldTestService.getStatusText(status);
  }

  getTestTypeBadgeClass(type: FieldTestType | null | undefined): string {
    return this.fieldTestService.getTestTypeBadgeClass(type);
  }

  getTestTypeLabel(type: FieldTestType | null | undefined): string {
    return this.fieldTestService.getTestTypeLabel(type);
  }

  getPassedChecklistCount(test: FieldTest): number {
    return this.fieldTestService.getPassedChecklistCount(test);
  }

  /**
   * Get the display name for applicable aircraft field
   * Returns drone name if ID matches, otherwise returns raw value
   */
  getAircraftDisplayName(applicableAircraft: string | null | undefined): string {
    if (!applicableAircraft || applicableAircraft === 'all') {
      return 'All Aircraft';
    }

    // Try to find a matching drone by ID
    const drone = this.droneService.getDroneById(applicableAircraft);
    if (drone?.name) {
      return drone.name;
    }

    // Return raw value if not found (might be a legacy string like "DJI Agras T40")
    return applicableAircraft;
  }
}
